import { appConfig, auth } from "../../db.js";

import {
    DateValue
} from "@comp/index.js";
import ItemListView from "../../components/ItemListView/ItemListView.vue";
import ItemView from "../../components/ItemView/ItemView.vue";
import { get } from "lodash"
import { getTenantClaim } from "../../authState.js";
import { customerPortalVariantVisible } from "./variantVisibility.js";
import {
    standardDateLocal
} from "../../utils.js";

const categories = {
    DesignItems: ["Documents", "Drawings", "SADDs", "Effectivities"],
    EquipmentAssessments: ["EARs", "EADTs", "PartNumbers", "Manufacturers"],
    DesignQueries: ["Concessions", "DQNs", "ServiceBulletins", "DOIs"]
};

const labels = {
    DesignItems: "Design Items",
    EquipmentAssessments: "Equipment Assessments",
    DesignQueries: "Design Queries"
};

const defaultTableSettings = {
    columnState: [{
        headerName: "Number",
        field: "Number",
        width: 180
    }, {
        headerName: "Title",
        field: "Title",
        minWidth: 280,
        flex: 1
    }, {
        headerName: "Type",
        field: "Type",
        width: 130
    }, {
        headerName: "Completed",
        field: "Completed",
        type: "bool",
        width: 130
    }]
};

function linkedDocumentRevisionCompleted(params) {
    const linkedRevision = get(params.data, params.column.colDef.field, params.data.RevisionID);
    return get(params.data, `Revisions.${linkedRevision}.Quality.Approver.Completed`, false);
}

function linkedDrawingRevisionCompleted(params) {
    const linkedRevision = get(params.data, params.column.colDef.field, params.data.RevisionID);
    return get(params.data, `Revisions.${linkedRevision}.Quality.Approver.Completed`, false);
}


const getEntitySpecificHeaders = (entity) => {
    switch (entity) {
        case "Documents":
            return [
                {
                    "headerName": "Linked Revision",
                    fieldGetter({ pageItem }) {
                        return `Links.${pageItem.ID}`
                    },
                    valueFormatter(params) {
                        let linkedRev = get(params.data, params.column.colId);
                        let revision = get(params.data, `Revisions.${linkedRev}`);
                        if (!!revision) {
                            return revision.Revision === params.data.Revision ? revision.Revision : `${revision.Revision} (Revision ${params.data.Revision} Available)`
                        } else return params.data.Revision
                    }
                }, {
                    "headerName": "Revision Completed",
                    "type": "bool",
                    fieldGetter({ pageItem }) {
                        return `Links.${pageItem.ID}`
                    },
                    valueGetter: linkedDocumentRevisionCompleted,
                    valueFormatter: linkedDocumentRevisionCompleted
                }
            ];
        case "Drawings":
            return [
                {
                    "headerName": "Linked Revision",
                    fieldGetter({ pageItem }) {
                        return `Links.${pageItem.ID}`
                    },
                    valueFormatter(params) {
                        let linkedRev = get(params.data, params.column.colDef.field);
                        let revision = get(params.data, `Revisions.${linkedRev}`);
                        return revision?.Revision || params.data.Revision;
                    }
                }, {
                    "headerName": "Compiler Sign Off",
                    "type": "bool",
                    fieldGetter({ pageItem }) {
                        return `Links.${pageItem.ID}`
                    },
                    valueGetter: linkedDrawingRevisionCompleted,
                    valueFormatter: linkedDrawingRevisionCompleted
                }, {
                    "headerName": "Checker Sign Off",
                    "type": "bool",
                    fieldGetter({ pageItem }) {
                        return `Links.${pageItem.ID}`
                    },
                    valueGetter: linkedDrawingRevisionCompleted,
                    valueFormatter: linkedDrawingRevisionCompleted
                }, {
                    "headerName": "Approver Sign Off",
                    "type": "bool",
                    fieldGetter({ pageItem }) {
                        return `Links.${pageItem.ID}`
                    },
                    valueGetter: linkedDrawingRevisionCompleted,
                    valueFormatter: linkedDrawingRevisionCompleted
                    }
            ];

        default:
            return [];
    }
};

const makeAttachable = (entity) => {
    const base =  {
        label: entity,
        key: `CustomerItems.${entity}`,
        local: true,
        headers: [
            { headerName: "Number", field: "Number" },
            { headerName: "Title", field: "Title" },
            { headerName: "Revision / Issue No.", field: "Revision" }
        ],
        actions: [],
        rowActions: []
    };

    const entitySpecificHeaders = getEntitySpecificHeaders(entity);
    return {
        ...base,
        headers: [...base.headers, ...entitySpecificHeaders]
    };
};

const linkedEntities = ({ store, currentRoute }, variant) => {
    const customerId = store.state.user?.CustomerID;
    const designChangeId = currentRoute.params.id;
    const permissions = store.state.user?.Permissions?.[variant] || {};
    if (!customerId || !designChangeId) return [];

    return Object.entries(categories).flatMap(([category, entities]) => {
        if (permissions[category] !== true) return [];
        return entities.map(entity => ({
            entity,
            path: `CustomerPortal/${customerId}/${variant}/${category}/${designChangeId}/${entity}`
        }));
    });
};

const makeRoutes = (variant, variantLabel) => [{
    path: `/CustomerPortal/${variant}/DesignChanges`,
    component: { ...ItemListView },
    name: `Customer ${variantLabel} Design Changes`,
    meta: {
        app: "CustomerPortal",
        name: `${variantLabel} Design Changes`,
        sideNav: true,
        sideNavSection: "Projects",
        sideNavPosition: variant === "stc" ? 0 : 1,
        appSwitcher: true,
        appSwitcherPosition: 2,
        icon: "database",
        customerPortal: true,
        readOnly: true,
        show: (user) => {
            const visible = customerPortalVariantVisible(user, variant);
            const variantPermissions = get(user?.Permissions, variant);
            if (variantPermissions) {
                console.log(variant, variantPermissions, visible);
            }
            return visible;
        },
        applyDesignChangeFilter: false,
        tableSettings: `CustomerPortal.${variant}.DesignChanges`,
        defaultTableSettings,
        persistTableSettings: false,
        dataPath: `CustomerPortal/${variant}/DesignChanges`,
        selectionRoute: `CustomerPortal/${variant}/DesignChanges/{ID}`,
        hidden(user) {

            return user?.CustomerID == undefined;
        },
        async lock(user) {
            // Require customer class on this tenant
            // Migration window: getTenantClaim handles both nested and flat structures
            const authUser = auth.currentUser;
            if (!authUser) return [true, "Not authenticated"];

            try {
                const idTokenResult = await authUser.getIdTokenResult();
                const claims = idTokenResult?.claims || {};
                const tenantKey = appConfig.appPrefix;
                const tenantClaim = getTenantClaim(claims, tenantKey);

                if (!tenantClaim?.customer) {
                    return [true, "Customer access required for this tenant"];
                }
            } catch (e) {
                console.error("Failed to check customer claims:", e);
                return [true, "Unable to verify customer access"];
            }

            return [false];
        },
        menuActions: []
    }
}, {
    path: `/CustomerPortal/${variant}/DesignChanges/:id`,
    component: ItemView,
    name: `Customer ${variantLabel} Design Change`,
    meta: {
        app: "CustomerPortal",
        name: `${variantLabel} Design Changes`,
        customerPortal: true,
        readOnly: true,
        addToRecents: false,
        preventAttachedItemsActions: true,
        async lock(user) {
            // Require customer class on this tenant
            const authUser = auth.currentUser;
            if (!authUser) return [true, "Not authenticated"];

            try {
                const idTokenResult = await authUser.getIdTokenResult();
                const claims = idTokenResult?.claims || {};
                const tenantKey = appConfig.appPrefix;
                const tenantClaim = getTenantClaim(claims, tenantKey);

                if (!tenantClaim?.customer) {
                    return [true, "Customer access required for this tenant"];
                }
            } catch (e) {
                console.error("Failed to check customer claims:", e);
                return [true, "Unable to verify customer access"];
            }

            return [false];
        },
        async dataPathOverride(route, { store }) {
            return `CustomerPortal/${store.state.user.CustomerID}/${variant}/DesignChanges/${route.params.id}`;
        },
        linkedDataWatchLocations(context) {
            return linkedEntities(context, variant).map(({ path }) => path);
        },
        linkedData(context) {
            return {
                CustomerItems: Object.fromEntries(
                    linkedEntities(context, variant).map(({ entity, path }) => [entity, context.store.state[path] || {}])
                )
            };
        },

        title(item) {
            return `${item.Number}: ${item.Title}`;
        },
        dataFields: [{ label: "Number", key: "Number" }, {
            label: "Title",
            key: "Title",
            class: "md:col-span-2"
        }, {
            label: "Type",
            key: "Type"
        }, {
            label: "Approval Number",
            key: "EASANumber"
        }, {
            label: "Completed",
            key: "Completed",
            type: "bool"
        }],
        attachedItems: Object.fromEntries(Object.entries(categories).map(([category, entities]) => [
            labels[category],
            {
                attachables: entities.map(makeAttachable),
                show({ user }) {
                    return user?.Permissions?.[variant]?.[category] === true;
                }
            }
        ])),
        menuActions: []
    }
}];

const formsDataFields = [{
    "label": "Form Number",
    "key": "FormNumber"
}, {
    "label": "Name",
    "key": "NameShort"
}, {
    "label": "Revision",
    "key": "Revision"
}, {
    "label": "Last Update",
    compute({ pageItem }) {
        const data = get(pageItem, `Revisions.${pageItem.RevisionID}.Date`, null);
        if (!data) return "No Date";
        return (new Date(data)).toLocaleDateString("en-gb", standardDateLocal);
    }
}];

const formRevisions = {
    "label": "Revisions",
    "key": "Revisions",
    "local": true,
    "headers": [{
        "headerName": "Revision",
        "field": "Revision",
        "sort": "desc"
    }, {
        "headerName": "Date",
        "field": "Date",
        "filter": "agDateColumnFilter",
        "cellRenderer": DateValue
    }, {
        "headerName": "Reason",
        "field": "Reason"
    }],
    "rowActions": [],
    "actions": []
};


export default [
    ...makeRoutes("stc", "Standard"),
    ...makeRoutes("military", "Military"),
    {
        "path": "/CustomerPortal/Forms",
        "component": { ...ItemListView },
        "name": "Form Templates CP",
        "meta": {
            "app": "CustomerPortal",
            "name": "Form Templates CP",
            "sideNav": true,
            "sideNavSection": "Forms, Handbooks & Work Instructions",
            "appSwitcher": false,
            "icon": "database",
            "tableSettings": "Forms",
            "dataPath": "Database/DocumentTypes",
            "dataPathOverride": "Database/DocumentTypes/:id",
            "selectionRoute": "CustomerPortal/Forms/{ID}",
            "menuActions": [],
            // lock(user) {
            //     return user?.Permissions
            // }
        }
    }, {
        "path": "/CustomerPortal/Forms/:id",
        "component": ItemView,
        "name": "Form cp",
        "meta": {
            "app": "CustomerPortal",
            "name": "Forms cp",
            "dataPath": "Database/DocumentTypes",
            "dataPathOverride": "Database/DocumentTypes/:id",
            title(item) {
                return `Forms: ${item.NameShort}`
            },
            "attachedItems": {
                "Revisions": {
                    "attachables": [formRevisions]
                }
            },
            "dataFields": formsDataFields,
            "menuActions": [{
                "label": "Download Latest",
                async action({ db, pageItem, toast }) {
                    if (!pageItem.RevisionID || !pageItem.Revisions?.[pageItem.RevisionID]?.File) {
                        toast.warning("No file available for this form template.");
                        return;
                    }
                    let rev = pageItem.Revisions[pageItem.RevisionID];
                    let url = await db.download(`Database/DocumentTypes/${pageItem.ID}/Revisions/${pageItem.RevisionID}/${rev.File}`);
                    window.open(url, "__blank");
                }
            }]
        }
    },
    {
        "path": "/CustomerPortal/Handbooks",
        "component": { ...ItemListView },
        "name": "Handbooks CP",
        "meta": {
            "app": "CustomerPortal",
            "name": "Handbooks CP",
            "sideNav": true,
            "sideNavSection": "Quality System",
            "appSwitcher": false,
            "icon": "database",
            "tableSettings": "Handbooks",
            "dataPath": "Database/Handbooks",
            "selectionRoute": "CustomerPortal/Handbooks/{ID}",
            "menuActions": []
        }
    }, {
        "path": "/CustomerPortal/Handbooks/:id",
        "component": ItemView,
        "name": "Handbook CP",
        "meta": {
            "app": "CustomerPortal",
            "name": "Handbook CP",
            "dataPath": "Database/Handbooks",
            "dataPathOverride": "Database/Handbooks/:id",
            "selectionRoute": "Database/Handbooks/{ID}",
            "storeData": ["Database/MFARQuestions"],
            "formMetadata": {
                "parentType": "Handbooks",
            },
            title(item) {
                return `Handbooks: ${item.Name}`;
            },
            "attachedItems": {
                "Additional Data": {
                    "attachables": []
                }
            },
            "dataFields": [],
            "menuActions": []
        }
    }
];
